import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { UserAvatar } from "@/components/crm/Badges";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Loader2, BarChart3, Pencil, Phone } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["#3B82F6", "#EC4899", "#10B981", "#F59E0B", "#8B5CF6", "#EF4444", "#06B6D4", "#F97316"];

export default function Team() {
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", role: "commerciale", color: COLORS[0], work_phone: "" });
  const [saving, setSaving] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", work_phone: "", role: "commerciale", color: COLORS[0], active: true });
  const [editSaving, setEditSaving] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.first_name.trim() || !form.email.trim()) { toast.error("Nome ed email obbligatori"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "", role: "commerciale", color: COLORS[0], work_phone: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setSaving(false);
  };

  const openEdit = (u) => {
    const parts = (u.name || "").trim().split(" ");
    setEditUser(u);
    setEditForm({
      first_name: u.first_name ?? (parts[0] || ""),
      last_name: u.last_name ?? (parts.slice(1).join(" ") || ""),
      email: u.email || "",
      work_phone: u.work_phone || "",
      role: u.role || "commerciale",
      color: u.color || COLORS[0],
      active: u.active,
    });
  };

  const saveEdit = async () => {
    if (!editForm.first_name.trim()) { toast.error("Nome obbligatorio"); return; }
    if (!editForm.email.trim()) { toast.error("Email obbligatoria"); return; }
    setEditSaving(true);
    try {
      await api.put(`/users/${editUser.id}`, editForm);
      toast.success("Profilo aggiornato");
      setEditUser(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
    setEditSaving(false);
  };

  const toggleActive = async (u) => {
    await api.put(`/users/${u.id}`, { active: !u.active });
    toast.success(u.active ? "Utente disattivato" : "Utente attivato");
    load();
  };

  if (!users) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tight">Team</h1>
          <p className="text-sm text-muted-foreground">{users.length} utenti</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-user-btn" className="gap-1"><Plus className="w-4 h-4" />Nuovo utente</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Nuovo utente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nome</Label><Input data-testid="user-name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Cognome</Label><Input data-testid="user-last-name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Email aziendale</Label><Input data-testid="user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Password</Label><Input data-testid="user-password" type="text" placeholder="Almeno 6 caratteri" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Numero di telefono di lavoro</Label><Input data-testid="user-work-phone" type="tel" placeholder="es. +39 333 1234567" value={form.work_phone} onChange={(e) => setForm({ ...form, work_phone: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="commerciale">Commerciale</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Colore</Label>
                  <div className="flex gap-1 flex-wrap pt-1">{COLORS.map((c) => <button key={c} onClick={() => setForm({ ...form, color: c })} className={`w-6 h-6 rounded-full ${form.color === c ? "ring-2 ring-offset-2 ring-ring" : ""}`} style={{ background: c }} />)}</div>
                </div>
              </div>
            </div>
            <DialogFooter><Button data-testid="user-save" onClick={create} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Crea utente</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-lg border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-xs text-muted-foreground text-left"><th className="px-4 py-2 font-medium">Utente</th><th className="px-3 py-2 font-medium">Email</th><th className="px-3 py-2 font-medium">Tel. lavoro</th><th className="px-3 py-2 font-medium">Ruolo</th><th className="px-3 py-2 font-medium text-center">Performance</th><th className="px-3 py-2 font-medium text-center">Modifica</th><th className="px-3 py-2 font-medium text-right">Attivo</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`user-row-${u.id}`} className="border-b last:border-0 hover:bg-muted/40">
                <td className="px-4 py-3">
                  {u.role === "commerciale" ? (
                    <button onClick={() => navigate(`/performance/${u.id}`)} data-testid={`user-open-${u.id}`} className="flex items-center gap-2 hover:underline">
                      <UserAvatar user={u} size={28} /><span className="font-medium">{u.name}</span>
                    </button>
                  ) : (
                    <div className="flex items-center gap-2"><UserAvatar user={u} size={28} /><span className="font-medium">{u.name}</span></div>
                  )}
                </td>
                <td className="px-3 py-3 text-muted-foreground">{u.email}</td>
                <td className="px-3 py-3" data-testid={`user-phone-${u.id}`}>
                  {u.work_phone ? (
                    <a href={`tel:${u.work_phone}`} className="inline-flex items-center gap-1 text-primary hover:underline" data-testid={`user-phone-link-${u.id}`}>
                      <Phone className="w-3.5 h-3.5" />{u.work_phone}
                    </a>
                  ) : (
                    <span className="text-muted-foreground text-xs">—</span>
                  )}
                </td>
                <td className="px-3 py-3"><span className="capitalize text-xs px-2 py-0.5 rounded bg-secondary">{u.role}</span></td>
                <td className="px-3 py-3 text-center">
                  {u.role === "commerciale" ? (
                    <Button variant="outline" size="sm" className="h-7 gap-1 text-xs" data-testid={`user-perf-${u.id}`} onClick={() => navigate(`/performance/${u.id}`)}>
                      <BarChart3 className="w-3.5 h-3.5" /> Dashboard
                    </Button>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-3 py-3 text-center">
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`user-edit-${u.id}`} onClick={() => openEdit(u)}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                </td>
                <td className="px-3 py-3 text-right"><Switch checked={u.active} onCheckedChange={() => toggleActive(u)} data-testid={`toggle-user-${u.id}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Modifica utente</DialogTitle></DialogHeader>
          {editUser && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nome</Label><Input data-testid="edit-user-name" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Cognome</Label><Input data-testid="edit-user-last-name" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Email aziendale</Label><Input data-testid="edit-user-email" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
              <div className="space-y-1"><Label className="text-xs">Numero di telefono di lavoro</Label><Input data-testid="edit-user-work-phone" type="tel" placeholder="es. +39 333 1234567" value={editForm.work_phone} onChange={(e) => setEditForm({ ...editForm, work_phone: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Ruolo</Label>
                  <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}><SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="commerciale">Commerciale</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent></Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Colore</Label>
                  <div className="flex gap-1 flex-wrap pt-1">{COLORS.map((c) => <button key={c} onClick={() => setEditForm({ ...editForm, color: c })} className={`w-6 h-6 rounded-full ${editForm.color === c ? "ring-2 ring-offset-2 ring-ring" : ""}`} style={{ background: c }} />)}</div>
                </div>
              </div>
              <div className="flex items-center justify-between rounded-md border px-3 py-2">
                <Label className="text-xs">Stato attivo</Label>
                <Switch data-testid="edit-user-active" checked={editForm.active} onCheckedChange={(v) => setEditForm({ ...editForm, active: v })} />
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="edit-user-save" onClick={saveEdit} disabled={editSaving}>{editSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
